import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect } from "react-router-dom";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import HttpService from "../../services/httpService";
import { ROLENAME } from "../../utils/index";
import {
    makeStyles,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";
import { CSVLink } from "react-csv";
import { DownloadOutlined, CarryOutOutlined, CalendarOutlined } from "@ant-design/icons";

const ALLOWED_ROLES = [ROLENAME.OperationDesignManager, ROLENAME.AGM_Design];

const useStyles = makeStyles(() => ({
    pageWrapper: {
        padding: "2rem",
        borderRadius: "10px",
        backgroundColor: "#fff",
        marginBottom: "1.5rem",
    },
    headerBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "26px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        background: "linear-gradient(135deg, #1C375D 0%, #2f5583 100%)",
        boxShadow: "0 6px 18px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.3px",
        fontSize: "28px",
    },
    titleIcon: {
        fontSize: "26px",
        color: "#F47E46",
    },
    subtitle: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginTop: "12px",
        padding: "5px 14px",
        borderRadius: "20px",
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "12.5px",
        fontWeight: 600,
    },
    noteBanner: {
        textAlign: "center",
        padding: "8px 20px",
        borderRadius: "8px",
        marginBottom: "20px",
        fontSize: "15px",
    },
    noteBannerMain: {
        fontWeight: 700,
    },
    noteBannerSub: {
        display: "block",
        marginTop: "4px",
        fontWeight: 500,
        fontSize: "15px",
        opacity: 0.85,
    },
    noteBannerPending: {
        backgroundColor: "#fff3e0",
        color: "#7a3e00",
        border: "1px solid #ff9800",
    },
    noteBannerClear: {
        backgroundColor: "#e8f5e8",
        color: "#1b5e20",
        border: "1px solid #2e7d32",
    },
    noteBannerLoading: {
        backgroundColor: "#eef2f7",
        color: "#1c375d",
        border: "1px solid #c7d2e0",
    },
    cardsWrapper: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "18px",
        padding: "26px",
        background: "#f0f4f8",
        borderRadius: "16px",
    },
    card: {
        flex: "0 0 calc(25% - 14px)",
        minHeight: "140px",
        borderRadius: "14px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "22px 16px",
        position: "relative",
        userSelect: "none",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 10px 22px rgba(0,0,0,0.16)",
        },
    },
    cardClickable: { cursor: "pointer" },
    cardOrange: { backgroundColor: "#fde8d8" },
    cardGreen:  { backgroundColor: "#d4edda" },
    // Small folded-corner ribbon accent in the top-right, echoing the "sticky-note" look.
    cardCorner: {
        position: "absolute",
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderWidth: "0 22px 22px 0",
        borderColor: "transparent",
    },
    cardCornerOrange: { borderRightColor: "#F47E46" },
    cardCornerGreen:  { borderRightColor: "#355025" },
    cardCount: {
        fontSize: "34px",
        fontWeight: 800,
        color: "#1C375D",
        lineHeight: 1.1,
        marginBottom: "8px",
    },
    cardLabel: {
        fontSize: "13px",
        fontWeight: 700,
        color: "#1C375D",
        textAlign: "center",
        lineHeight: 1.4,
    },
    headerCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.2)",
        borderBottom: "1px solid #fff",
        position: "sticky",
        top: 0,
        zIndex: 2,
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    bodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    tableRowEven: { backgroundColor: "#f9f9f9" },
    tableRowOdd:  { backgroundColor: "#fff" },
    csv: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "12px",
    },
    noData: {
        textAlign: "center",
        padding: "40px",
        color: "#94a3b8",
        fontSize: "15px",
    },
    modalInfoNote: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        borderRadius: "8px",
        marginBottom: "14px",
        backgroundColor: "#e8f0fe",
        color: "#1c375d",
        border: "1px solid #b6cdfb",
        fontSize: "13px",
        fontWeight: 500,
    },
}));

// Cards styled orange (needs attention) vs green (on track). Most orange cards also block the
// sidebar when count > 0 (see fetchPendingTask's dashboardFrozen flag) — "approvalsPending" is
// the one exception, kept orange for visibility but deliberately excluded from that lock
// calculation (see BLOCKING_KEYS below).
const ORANGE_KEYS = new Set(["dna", "ikom", "approvalsPending", "lastDealActivity", "ticketsPending"]);
const BLOCKING_KEYS = new Set(["dna", "ikom", "lastDealActivity", "ticketsPending"]);

function PendingCard({ classes, cardKey, count, label, onClick, loading }) {
    const isOrange = ORANGE_KEYS.has(cardKey);
    return (
        <Paper
            onClick={!loading ? onClick : undefined}
            className={`${classes.card} ${isOrange ? classes.cardOrange : classes.cardGreen} ${!loading ? classes.cardClickable : ""}`}
        >
            <span className={`${classes.cardCorner} ${isOrange ? classes.cardCornerOrange : classes.cardCornerGreen}`} />
            {loading ? (
                <Skeleton variant="rect" width={60} height={44} style={{ borderRadius: 6, marginBottom: 10, backgroundColor: "rgba(28,55,93,0.12)" }} />
            ) : (
                <Typography className={classes.cardCount}>{count}</Typography>
            )}
            <Typography className={classes.cardLabel}>
                {loading ? <Skeleton width={100} style={{ margin: "0 auto", backgroundColor: "rgba(28,55,93,0.12)" }} /> : label}
            </Typography>
        </Paper>
    );
}

// ── Column definitions per card type ──────────────────────────────────────────

const COLS_LEAD = [
    { label: "Sl. No.",        key: "slno" },
    { label: "Customer Name",  key: "customerName" },
    { label: "Project No.",    key: "erpProjectNo" },
    { label: "Lead No.",       key: "lead_no" },
    { label: "City",           key: "cityName" },
    { label: "Exp. Center",    key: "experienceCenterName" },
    { label: "Current Stage",  key: "currentStage" },
    { label: "Value (INR L)",  key: "value" },
    { label: "Sales Won Date", key: "salesWonDate" },
    { label: "TAT (Days)",     key: "tatDays" },
];

const COLS_LEAD_ACTIVITY = [
    { label: "Sl. No.",        key: "slno" },
    { label: "Customer Name",  key: "customerName" },
    { label: "Project No.",    key: "erpProjectNo" },
    { label: "Lead No.",       key: "lead_no" },
    { label: "City",           key: "cityName" },
    { label: "Exp. Center",    key: "experienceCenterName" },
    { label: "Current Stage",  key: "currentStage" },
    { label: "Last Activity",  key: "lastActivity" },
    { label: "TAT (Days)",     key: "tatDays" },
];

const COLS_APPROVAL = [
    { label: "Sl. No.",           key: "slno" },
    { label: "Customer Name",     key: "customerName" },
    { label: "Project No.",       key: "erpProjectNo" },
    { label: "Lead No.",          key: "lead_no" },
    { label: "City",              key: "cityName" },
    { label: "Exp. Center",       key: "experienceCenterName" },
    { label: "Category",          key: "category" },
    { label: "Approval Status",   key: "approvalStatus" },
    { label: "Pending Since",     key: "pendingSince" },
    { label: "TAT (Days)",        key: "tatDays" },
];

const COLS_D2E = [
    { label: "Sl. No.",       key: "slno" },
    { label: "Customer Name", key: "customerName" },
    { label: "Project No.",   key: "erpProjectNo" },
    { label: "Lead No.",      key: "lead_no" },
    { label: "City",          key: "cityName" },
    { label: "Exp. Center",   key: "experienceCenterName" },
    { label: "IDSO Date",     key: "idsoDate" },
    { label: "TAT (Days)",    key: "tatDays" },
    { label: "D2E Status",    key: "d2eStatus" },
];

const COLS_FLAG = [
    { label: "Sl. No.",      key: "slno" },
    { label: "Lead No.",     key: "leadNo" },
    { label: "Project No.",  key: "erpProjectNo" },
    { label: "Raised By",    key: "raisedBy" },
    { label: "Flagged To",   key: "flagTo" },
    { label: "Status",       key: "status" },
    { label: "Created At",   key: "createdAt" },
];

const COLS_TICKET = [
    { label: "Sl. No.",       key: "slno" },
    { label: "Ticket No.",    key: "ticketNo" },
    { label: "Title",         key: "title" },
    { label: "Type",          key: "ticketType" },
    { label: "Assigned To",   key: "assignedTo" },
    { label: "Status",        key: "status" },
    { label: "Created At",    key: "createdAt" },
];

// Deep-links into /Task-Management-DashBoard, pre-selecting its ribbon tab + Tag Status
// filter (that page reads ?tab=&tagStatus= on load) so a pending ticket here opens already
// filtered to Pending — on the Customer ribbon for customer tickets, Internal otherwise.
function taskManagementDashboardLink(ticketType) {
    const tab = ticketType === "Customer" ? "Customer Tickets Assigned To Me" : "Tickets Assigned To Me";
    return `/Task-Management-DashBoard?tab=${encodeURIComponent(tab)}&tagStatus=Pending`;
}

function getColumns(type) {
    if (type === "activity")  return COLS_LEAD_ACTIVITY;
    if (type === "approval")  return COLS_APPROVAL;
    if (type === "d2e")       return COLS_D2E;
    if (type === "flag")      return COLS_FLAG;
    if (type === "ticket")    return COLS_TICKET;
    return COLS_LEAD;
}

// Modal types whose lead-no/ticket-no cells are clickable deep links — flagged for the user
// with an explanatory note inside the modal, naming the actual destination page.
const MODAL_NOTE_TEXT = {
    approval: "On clicking the lead no., it'll redirect to the My Approval Dashboard page.",
    flag: "On clicking the lead no., it'll redirect to the Flagged Projects page.",
    ticket: "On clicking the ticket no., it'll redirect to the Task Management Dashboard page.",
};

function normalizeRows(data, type) {
    if (!Array.isArray(data)) return [];
    return data.map((item, idx) => {
        if (type === "flag") {
            return {
                slno:        idx + 1,
                leadId:      item.leadId?._id || item.leadId,
                leadNo:      item.leadId?.lead_no || item.leadNo || "-",
                erpProjectNo:item.leadId?.erpProjectNo || "-",
                raisedBy:    item.raisedBy?.name || "-",
                flagTo:      item.flagTo?.name || "-",
                status:      item.status || "-",
                createdAt:   item.createdAt ? moment(item.createdAt).format("DD-MM-YYYY") : "-",
            };
        }
        if (type === "ticket") {
            return {
                slno:             idx + 1,
                leadId:           item.leadId?._id || item.leadId,
                ticketNo:         item.uniqueTicketNo || "-",
                uniqueTicketNo:   item.uniqueTicketNo || "",
                title:            item.ticketTitle || "-",
                ticketType:       item.ticketType || "-",
                assignedTo:       item.assignedTo?.name || "-",
                status:           item.status || "-",
                createdAt:        item.createdAt ? moment(item.createdAt).format("DD-MM-YYYY") : "-",
            };
        }
        if (type === "activity") {
            return {
                slno:                idx + 1,
                leadId:              item.leadId,
                customerName:        item.customerName || "-",
                erpProjectNo:        item.erpProjectNo || "-",
                lead_no:             item.lead_no || "-",
                cityName:            item.cityName || "-",
                experienceCenterName:item.experienceCenterName || "-",
                currentStage:        item.currentStage || "-",
                lastActivity:        item.lastActivityDate ? moment(item.lastActivityDate).format("DD-MM-YYYY") : "No Activity",
                tatDays:             item.tatDays != null ? item.tatDays : "-",
            };
        }
        if (type === "approval") {
            return {
                slno:                idx + 1,
                leadId:              item.leadId,
                customerName:        item.customerName || "-",
                erpProjectNo:        item.erpProjectNo || "-",
                lead_no:             item.lead_no || "-",
                cityName:            item.cityName || "-",
                experienceCenterName:item.experienceCenterName || "-",
                category:            item.category || "-",
                approvalStatus:      item.approvalStatus || "-",
                pendingSince:        item.pendingSince ? moment(item.pendingSince).format("DD-MM-YYYY") : "-",
                tatDays:             item.tatDays != null ? item.tatDays : "-",
            };
        }
        if (type === "d2e") {
            return {
                slno:                idx + 1,
                leadId:              item.leadId,
                customerName:        item.customerName || "-",
                erpProjectNo:        item.erpProjectNo || "-",
                lead_no:             item.lead_no || "-",
                cityName:            item.cityName || "-",
                experienceCenterName:item.experienceCenterName || "-",
                idsoDate:            item.internalDesignSignOffDate ? moment(item.internalDesignSignOffDate).format("DD-MM-YYYY") : "-",
                tatDays:             item.tatDays != null ? item.tatDays : "-",
                d2eStatus:           item.designToExecutionLogsStatus || "-",
            };
        }
        // default: COLS_LEAD
        return {
            slno:                idx + 1,
            leadId:              item.leadId,
            customerName:        item.customerName || "-",
            erpProjectNo:        item.erpProjectNo || "-",
            lead_no:             item.lead_no || "-",
            cityName:            item.cityName || "-",
            experienceCenterName:item.experienceCenterName || "-",
            currentStage:        item.currentStage || "-",
            value:               item.staticSalesWonBudget != null ? Number(item.staticSalesWonBudget).toFixed(2) : "-",
            salesWonDate:        item.leadWonDate ? moment(item.leadWonDate).format("DD-MM-YYYY") : "-",
            tatDays:             item.tatDays != null ? item.tatDays : "-",
        };
    });
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function OdmAgmPendingTask() {
    const classes  = useStyles();
    const dispatch = useDispatch();
    const { userDetails } = useSelector((state) => state.user);

    const userRoleName = userDetails?.roles?.[0]?.name || "";
    const isODM        = userRoleName === ROLENAME.OperationDesignManager;
    const isAllowed    = ALLOWED_ROLES.includes(userRoleName);

    const [cardsLoading, setCardsLoading] = useState(true);
    const [cardData, setCardData] = useState(null);
    const [modal, setModal] = useState({ open: false, title: "", data: [], type: "lead" });

    useEffect(() => { fetchPendingTask(); }, []);

    const fetchPendingTask = async () => {
        dispatch(setLoading(true));
        setCardsLoading(true);
        try {
            const res = await HttpService.odmAgmPendingTask();
            const data = res.data;
            setCardData(data);

            // Freeze sidebar if any blocking card has count > 0 — approvalsPending is
            // deliberately excluded (stays orange, doesn't lock the sidebar); see BLOCKING_KEYS.
            const orangeCount = (data?.dna?.count || 0)
                + (data?.ikom?.count || 0)
                + (data?.lastDealActivity?.count || 0)
                + (data?.ticketsPending?.count || 0);
            localStorage.setItem('dashboardFrozen', orangeCount > 0 ? 'true' : 'false');
        } catch (err) {
            dispatch(setSnackInfo(err?.response?.data?.message || "Something went wrong", "error"));
        } finally {
            setCardsLoading(false);
            dispatch(setLoading(false));
        }
    };

    if (userDetails?._id && !isAllowed) return <Redirect to="/unauthorized" />;

    const openModal  = (title, data, type) => setModal({ open: true, title, data, type });
    const closeModal = () => setModal({ open: false, title: "", data: [], type: "lead" });

    const columns    = getColumns(modal.type);
    const rows       = normalizeRows(modal.data, modal.type);
    const csvHeaders = columns.map(c => ({ label: c.label, key: c.key }));

    const dnaLabel      = isODM ? "DNA > 1 Day"                : "DNA > 2 Days";
    const ikomLabel     = isODM ? "IKOM > 1 Day post DNA"       : "IKOM > 2 Days post DNA";
    const ekomLabel     = isODM ? "EKOM > 2 Days post IKOM"     : "EKOM > 3 Days post IKOM";
    const activityLabel = isODM ? "Last Deal Activity > 7 Days"  : "Last Deal Activity > 15 Days";
    const d2eLabel      = isODM ? "D2E Pending > 10 Days (IDSO)" : "D2E Pending > 20 Days (IDSO)";

    const cards = [
        {
            cardKey: "dna",
            label:   dnaLabel,
            count:   cardData?.dna?.count ?? 0,
            onClick: () => openModal(dnaLabel, cardData?.dna?.data || [], "lead"),
        },
        {
            cardKey: "ikom",
            label:   ikomLabel,
            count:   cardData?.ikom?.count ?? 0,
            onClick: () => openModal(ikomLabel, cardData?.ikom?.data || [], "lead"),
        },
        {
            cardKey: "ekom",
            label:   ekomLabel,
            count:   cardData?.ekom?.count ?? 0,
            onClick: () => openModal(ekomLabel, cardData?.ekom?.data || [], "lead"),
        },
        {
            cardKey: "approvalsPending",
            label:   "Approvals Pending > 1 Day",
            count:   cardData?.approvalsPending?.count ?? 0,
            onClick: () => openModal("Approvals Pending > 1 Day", cardData?.approvalsPending?.data || [], "approval"),
        },
        {
            cardKey: "lastDealActivity",
            label:   activityLabel,
            count:   cardData?.lastDealActivity?.count ?? 0,
            onClick: () => openModal(activityLabel, cardData?.lastDealActivity?.data || [], "activity"),
        },
        {
            cardKey: "flagProjects",
            label:   "Flag Projects",
            count:   cardData?.flagProjects?.count ?? 0,
            onClick: () => openModal("Flag Projects", cardData?.flagProjects?.data || [], "flag"),
        },
        {
            cardKey: "ticketsPending",
            label:   "Tickets Pending",
            count:   cardData?.ticketsPending?.count ?? 0,
            onClick: () => openModal("Tickets Pending", cardData?.ticketsPending?.data || [], "ticket"),
        },
        {
            cardKey: "d2ePending",
            label:   d2eLabel,
            count:   cardData?.d2ePending?.count ?? 0,
            onClick: () => openModal(d2eLabel, cardData?.d2ePending?.data || [], "d2e"),
        },
    ];

    const orangePending = cards.reduce((sum, c) => sum + (ORANGE_KEYS.has(c.cardKey) ? (c.count || 0) : 0), 0);

    return (
        <Paper className={classes.pageWrapper}>
            <div className={classes.headerBanner}>
                <Typography className={classes.title}>
                    <CarryOutOutlined className={classes.titleIcon} />
                    Pending Tasks Dashboard
                </Typography>
                <Typography className={classes.subtitle}>
                    <CalendarOutlined />
                    {moment().format("MMMM YYYY")} — click any card to view details
                </Typography>
            </div>

            {/* ── Loading note ──────────────────────────────────────────────────── */}
            {cardsLoading && (
                <Typography className={`${classes.noteBanner} ${classes.noteBannerLoading}`}>
                    <span className={classes.noteBannerMain}>Loading...</span>
                </Typography>
            )}

            {/* ── Motivational note ────────────────────────────────────────────── */}
            {!cardsLoading && (
                <Typography
                    className={`${classes.noteBanner} ${orangePending > 0 ? classes.noteBannerPending : classes.noteBannerClear}`}
                >
                    <span className={classes.noteBannerMain}>
                        {orangePending > 0
                            ? "Hey, you're almost there! Clear the pending items below to move forward."
                            : "All clear! No pending tasks right now — great work."}
                    </span>
                    <span className={classes.noteBannerSub}>
                        {orangePending > 0
                            ? "Note: Orange cards keep your sidebar locked until they reach zero."
                            : "Your sidebar stays unlocked."}
                    </span>
                </Typography>
            )}

            {/* ── Cards ─────────────────────────────────────────────────────── */}
            <div className={classes.cardsWrapper}>
                {cards.map(card => <PendingCard key={card.cardKey} classes={classes} loading={cardsLoading} {...card} />)}
            </div>

            {/* ── Detail Modal ───────────────────────────────────────────────── */}
            <Dialog open={modal.open} onClose={closeModal} maxWidth="xl" fullWidth
                PaperProps={{ style: { borderRadius: "10px", maxHeight: "90vh" } }}>
                <DialogTitle disableTypography style={{ backgroundColor: "#1C375D", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px" }}>
                    <Typography style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>
                        {modal.title}
                        <span style={{ marginLeft: 12, fontSize: "13px", fontWeight: 400, opacity: 0.8 }}>
                            ({rows.length} records)
                        </span>
                    </Typography>
                    <IconButton size="small" onClick={closeModal} style={{ color: "#fff" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: "16px 20px" }}>
                    {(MODAL_NOTE_TEXT[modal.type] || rows.length > 0) && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: MODAL_NOTE_TEXT[modal.type] ? "space-between" : "flex-end",
                            gap: "16px",
                            flexWrap: "wrap",
                            marginBottom: "12px",
                        }}>
                            {MODAL_NOTE_TEXT[modal.type] && (
                                <Typography className={classes.modalInfoNote} style={{ marginBottom: 0, flex: "1 1 auto" }}>
                                    <strong>Note:</strong>&nbsp;{MODAL_NOTE_TEXT[modal.type]}
                                </Typography>
                            )}
                            {rows.length > 0 && (
                                <CSVLink data={rows} headers={csvHeaders}
                                    filename={`${modal.title.replace(/\s+/g, "_")}_${moment().format("YYYY-MM-DD")}.csv`}
                                    style={{ textDecoration: "none", flexShrink: 0 }}>
                                    <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                        <DownloadOutlined /> Download CSV
                                    </button>
                                </CSVLink>
                            )}
                        </div>
                    )}
                    {rows.length === 0 ? (
                        <Typography className={classes.noData}>No data found</Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {columns.map(col => (
                                            <TableCell key={col.key} className={classes.headerCell}>{col.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => (
                                        <TableRow key={idx} className={idx % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd}>
                                            {columns.map(col => (
                                                <TableCell key={col.key} className={classes.bodyCell}>
                                                    {col.key === "ticketNo" && row.uniqueTicketNo ? (
                                                        <a href={taskManagementDashboardLink(row.ticketType)} target="_blank" rel="noopener noreferrer"
                                                            style={{ color: "#1C375D", textDecoration: "underline", fontWeight: 600 }}>
                                                            {row[col.key]}
                                                        </a>
                                                    ) : modal.type === "flag" && (col.key === "lead_no" || col.key === "leadNo" || col.key === "erpProjectNo") ? (
                                                        <a href="/flagged-projects" target="_blank" rel="noopener noreferrer"
                                                            style={{ color: "#1C375D", textDecoration: "underline", fontWeight: 600 }}>
                                                            {row[col.key]}
                                                        </a>
                                                    ) : modal.type === "approval" && (col.key === "lead_no" || col.key === "erpProjectNo") ? (
                                                        <a href="/odm-agm-approvals" target="_blank" rel="noopener noreferrer"
                                                            style={{ color: "#1C375D", textDecoration: "underline", fontWeight: 600 }}>
                                                            {row[col.key]}
                                                        </a>
                                                    ) : (col.key === "lead_no" || col.key === "leadNo" || col.key === "erpProjectNo") && row.leadId ? (
                                                        <a href={`/project-logs/${row.leadId}`} target="_blank" rel="noopener noreferrer"
                                                            style={{ color: "#1C375D", textDecoration: "underline", fontWeight: 600 }}>
                                                            {row[col.key]}
                                                        </a>
                                                    ) : row[col.key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}
